<script setup lang="ts">
import { defineEmits, ref, onMounted } from 'vue'
import { NCard, NForm, NFormItem, NInput, NSwitch, NButton, NIcon, FormInst } from 'naive-ui';
import { ExternalLinkAlt, Edit as EditIconFa } from '@vicons/fa'
import { settingsFormRules } from "@/ts/formValidationRules";
import { onlyAllowNumberValidator } from "@/ts/validation";
import { editIcon } from "@/assets/utils/icons";

import { useAuthMeStore } from "@/stores/auth/me.store";
import { useUpdateClientStore } from "@/stores/client/profile-settings.store";
import { UserServerData } from "@/ts/interfaces/requests/user";

import NewPasswordForm from "@/components/NewPasswordForm.vue";

const emit = defineEmits([ 'submitAction' ])

const authMeStore = useAuthMeStore()
const updateClientStore = useUpdateClientStore()

const formRef = ref<FormInst | null>(null)
const showPasswordUpdateForm = ref<boolean>(false)

const formData = ref<UserServerData>({
  name: '',
  email: '',
  job_title: '',
  company: '',
  phone_number: '',
  meetings_link: '',
  email_notifications: true
})

const submit = () => {
  formRef.value?.validate((errors) => {
    if (!errors) {
      updateClientStore.edit(formData.value)
    }
  })
}

onMounted(async () => {
  await authMeStore.getUser()
  formData.value = authMeStore.user
})

</script>

<template>
  <section class="settings-form form">
    <n-card size="large" :bordered="false">
      <div class="grid-x">
        <h1 class="heading h1 form-title">
          Settings
        </h1>
      </div>

      <div class="grid-x">
        <div class="cell small-12 xxlarge-8">
          <n-form ref="formRef" :model="formData" :rules="settingsFormRules">
            <div class="form-section">
              <span class="section-title text-w-bold">
                MY PROFILE
              </span>

              <div class="input-group">
                <label class="body-text-lg text-w-bold">
                  Full Name
                </label>
                <n-form-item label="Full name" path="name">
                  <n-input v-model:value="formData.name" size="medium" round placeholder="Type the role title here" class="form-input">
                    <template #suffix>
                      <div class="input-icon" v-html="editIcon"></div>
                    </template>
                  </n-input>
                </n-form-item>
              </div>

              <div class="input-group">
                <label class="body-text-lg text-w-bold">
                  Corporate Email
                </label>
                <n-form-item label="Corporate Email" path="email">
                  <n-input v-model:value="formData.email" size="medium" round placeholder="YourCorporate@email.com" class="form-input" @keydown.enter.prevent>
                    <template #suffix>
                      <div class="input-icon" v-html="editIcon"></div>
                    </template>
                  </n-input>
                </n-form-item>
              </div>

              <div class="input-group">
                <label class="body-text-lg text-w-bold">
                  Job Title
                </label>
                <n-form-item label="Job Title" path="job_title">
                  <n-input v-model:value="formData.job_title" size="medium" round placeholder="Type here your job title" class="form-input" @keydown.enter.prevent>
                    <template #suffix>
                      <div class="input-icon" v-html="editIcon"></div>
                    </template>
                  </n-input>
                </n-form-item>
              </div>

              <div class="input-group">
                <label class="body-text-lg text-w-bold">
                  Company
                </label>
                <n-form-item label="Company" path="company">
                  <n-input v-model:value="formData.company" size="medium" round placeholder="Type your company" class="form-input" @keydown.enter.prevent>
                    <template #suffix>
                      <div class="input-icon" v-html="editIcon"></div>
                    </template>
                  </n-input>
                </n-form-item>
              </div>

              <div class="input-group">
                <label class="body-text-lg text-w-bold">
                  Direct Phone Number
                </label>
                <n-form-item label="Phone Number" path="phone_number">
                  <n-input v-model:value="formData.phone_number" size="medium" round placeholder="Type your phone number here" class="form-input" type="text" :allow-input="onlyAllowNumberValidator">
                    <template #suffix>
                      <div class="input-icon" v-html="editIcon"></div>
                    </template>
                  </n-input>
                </n-form-item>
              </div>

              <div class="input-group">
                <label class="body-text-lg text-w-bold">
                  Meetings Link
                </label>
                <n-form-item label="Company" path="meetings_link">
                  <n-input v-model:value="formData.meetings_link" size="medium" round placeholder="Type your company" class="form-input" @keydown.enter.prevent>
                    <template #suffix>
                      <div class="input-icon" v-html="editIcon"></div>
                    </template>
                  </n-input>
                </n-form-item>
              </div>
            </div>

            <div class="form-section">
              <span class="section-title text-w-bold">
                SECURITY AND PRIVACY
              </span>

              <n-card title="UPDATE PASSWORD" class="new-password-form" v-show="showPasswordUpdateForm" closable @close="showPasswordUpdateForm = false">
                <new-password-form :form-data="passwordFormData"></new-password-form>
              </n-card>

              <div class="button-group vertical">
                <div class="grid-x" v-show="!showPasswordUpdateForm">
                  <div class="cell small-12 large-6">
                    <n-button size="large" type="primary" :focusable="true" @click="showPasswordUpdateForm = true">
                      <template #icon>
                        <edit-icon-fa></edit-icon-fa>
                      </template>
                      UPDATE PASSWORD
                    </n-button>
                  </div>
                </div>

                <div class="grid-x">
                  <div class="cell small-12 large-6">
                    <n-button size="large" type="primary" :focusable="true">
                      <template #icon>
                        <n-icon>
                          <external-link-alt></external-link-alt>
                        </n-icon>
                      </template>
                      PRIVACY POLICY
                    </n-button>
                  </div>
                </div>
              </div>

            </div>

            <div class="form-section">
              <span class="section-title text-w-bold">
                NOTIFICATIONS
              </span>
              <div class="input-group grid-x">
                <div class="cell auto">
                  <label class="body-text-lg text-w-bold">
                    Receive Email Notifications
                  </label>
                </div>
                <div class="cell auto text-align-right">
                  <n-switch v-model:value="formData.email_notifications" />
                </div>
              </div>
            </div>
          </n-form>
        </div>
      </div>
    </n-card>

    <div class="button-container grid-x align-center">
      <div class="cell small-12 large-4">
        <n-button @click.prevent="submit" class="new-project-button" size="large" tabindex="0">
          SAVE
        </n-button>
      </div>
    </div>

  </section>
</template>

<style lang="scss" scoped>
.form {
  .form-title {
    margin-bottom: 50px;
  }

  .form-section {
    margin-bottom:28px;

    .section-title {
      display: block;
      margin-bottom: 25px;
    }
  }

  .input-group {
    margin-bottom: 6px;

    .n-input, .n-select {
      margin-top: 25px;
    }

    &.with-subtext {
      .n-input, .n-select {
        margin-top: 4px;
      }
    }

    .n-base-selection .n-base-suffix {
      right: 25px;
    }

    .n-input__suffix {
      .input-icon {
        width: 20px;
        height: 20px;
        display: flex;
      }
    }
  }
}

.button-container {
  margin-top: 28px;
}

.new-password-form {
  margin-bottom: 28px;
}
</style>